import {
  ChannelDetailContext,
  GraphDataContext,
  PositionContext,
  SelectionContext,
} from '@subsets/workspaces';
import { Node as NodeType } from '@subsets/workspaces/graphs/graph/types';
import { drag, select } from 'd3';
import { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Node } from './Node';

type DraggableNodeProps = NodeType & { disabled: boolean; index: number };
export const DraggableNode = memo((props: DraggableNodeProps) => {
  const { name, location, hash, nodeClass, disabled } = props;
  const { registerNodeReference, getNodePosition } = useContext(PositionContext);
  const ref = useRef<SVGSVGElement>();
  const container = useRef<HTMLDivElement>();
  const { links, updateNodeLocation, createLink } = useContext(GraphDataContext);
  const { isSelected, selectNode, selectedNodeList, selectedOutputPort } =
    useContext(SelectionContext);

  // Mirror selectedNodeList in a ref so d3 drag handlers (which capture closure) always
  // read the current set rather than a stale snapshot from when the effect last ran.
  const selectedNodeListRef = useRef(selectedNodeList);
  selectedNodeListRef.current = selectedNodeList;

  const { channelNodes } = useContext(ChannelDetailContext);
  const channelNode = channelNodes?.find((findNode) => findNode.name === nodeClass);
  const isNodeOutdated =
    Boolean(channelNode?.hash) &&
    Boolean(channelNode?.hash ? hash !== channelNode.hash : hash !== channelNode?.schemaHash);

  useEffect(() => {
    getNodePosition(name).next([location?.x, location?.y]);
  }, [location?.x, location?.y, name, getNodePosition]);

  useEffect(() => {
    const position$ = getNodePosition(name);
    let moved = false;
    const onDrag = (event: DragEvent & { dx: number; dy: number }) => {
      let isMultiple = selectedNodeListRef.current.size > 1;
      if (!isSelected(name)) {
        selectNode(name, event);
        isMultiple = false;
      }
      const { dx, dy } = event;
      if (dx === 0 && dy === 0) {
        return;
      }
      moved = true;
      selectedOutputPort.next(undefined);
      const list = isMultiple ? selectedNodeListRef.current : new Set([name]);
      list.forEach((id) => {
        const idPosition$ = getNodePosition(id);
        const [currentX, currentY] = idPosition$.getValue();
        idPosition$.next([currentX + dx, currentY + dy]);
      });
    };
    const onDragEnd = () => {
      if (moved) {
        const currentSelection = selectedNodeListRef.current;
        const isMultiple = currentSelection.size > 1;
        if (isMultiple) {
          Array.from(currentSelection).forEach((selectedNodeId, index) => {
            const idPosition$ = getNodePosition(selectedNodeId);
            updateNodeLocation(
              selectedNodeId,
              { x: idPosition$.value[0], y: idPosition$.value[1] },
              index === currentSelection.size - 1,
            );
          });
        } else {
          updateNodeLocation(name, { x: position$.value[0], y: position$.value[1] });
        }
      } else if (!isSelected(name)) {
        selectNode(name, {} as MouseEvent);
      }
    };
    select(ref.current).call(
      drag()
        .on('drag', !disabled ? onDrag : () => {})
        .on('end', !disabled ? onDragEnd : () => {}),
    );
    const sub = position$.subscribe(([currentX, currentY]) => {
      select(ref.current).attr('transform', `translate(${currentX}, ${currentY})`);
    });
    return () => {
      sub.unsubscribe();
    };
  }, [
    name,
    disabled,
    isSelected,
    selectNode,
    selectedOutputPort,
    getNodePosition,
    updateNodeLocation,
  ]);

  useEffect(() => {
    registerNodeReference(name, container);
  }, [name, container, registerNodeReference]);

  const state = useMemo(() => {
    const isSelectedAlone = selectedNodeList.size === 1 && isSelected(name);
    if (isSelectedAlone) {
      return 'focus';
    }
    if (isSelected(name)) {
      return 'active';
    }
    if (nodeClass === 'VolumeFile' || nodeClass === 'VolumeDirectory') {
      return 'default';
    }
    if (isNodeOutdated) {
      return 'outdated';
    }
    return 'default';
  }, [selectedNodeList, isSelected, name, nodeClass, isNodeOutdated]);

  const onOutputPortSelect = useCallback(
    (portId: string) => {
      if (disabled) {
        return;
      }
      selectedOutputPort.next({ portId, nodeId: name });
    },
    [disabled, name, selectedOutputPort],
  );

  const onInputPortSelect = useCallback(
    async (portId: string) => {
      const from = selectedOutputPort.getValue();
      if (!from) {
        return;
      }
      const isPortOfSameNode = from?.nodeId === name;
      if (isPortOfSameNode) {
        return;
      }
      const isLinkAlreadyCreatedBetweenNodes = links.some(
        (link) =>
          link.from.nodeId === from.nodeId &&
          link.from.portId === from.portId &&
          link.to.nodeId === name &&
          link.to.portId === portId,
      );
      selectedOutputPort.next(undefined);
      if (isLinkAlreadyCreatedBetweenNodes) {
        return;
      }
      await createLink({
        from,
        to: { portId, nodeId: name },
      });
    },
    [selectedOutputPort, createLink, links, name],
  );

  return (
    <Node
      ref={ref}
      {...props}
      container={container}
      state={state}
      onOutputPortSelect={onOutputPortSelect}
      onInputPortSelect={onInputPortSelect}
    />
  );
});
